import type { ReactNode } from 'react';
import styles from './Panel.module.css';

/* ==========================================================================
   Panel — every surface in the console is one of these or a grid of them.

   Header contract: `[title (count)] ……… [meta] [icon icon icon]`, one line,
   28px, icons always present. No subtitle. No description. A panel that needs
   two lines of header needs to be two panels.
   ========================================================================== */

interface PanelProps {
  title?: ReactNode;
  /** Rendered as `(14)` after the title. Counts belong on the title line. */
  count?: number | string | undefined;
  /** Right-aligned mono facts: liveness, latency, age. */
  meta?: ReactNode;
  /** Right-aligned icon cluster. Always rendered, never hover-revealed. */
  tools?: ReactNode;
  /** 1px indeterminate bar under the header; content stays put underneath. */
  loading?: boolean | undefined;
  /** True black ground for a 3D scene / video / map. */
  viewport?: boolean | undefined;
  /** Drop the panel border when the parent already draws the seam. */
  flush?: boolean | undefined;
  /** `none` for viewports and full-bleed tables; `tight` for a bare list. */
  pad?: 'default' | 'none' | 'tight' | undefined;
  scroll?: boolean | undefined;
  className?: string | undefined;
  children?: ReactNode;
}

export function Panel({
  title,
  count,
  meta,
  tools,
  loading = false,
  viewport = false,
  flush = false,
  pad = 'default',
  scroll = true,
  className,
  children,
}: PanelProps) {
  const hasHeader = title !== undefined || meta !== undefined || tools !== undefined;
  return (
    <section
      className={className === undefined ? styles.panel : `${styles.panel} ${className}`}
      data-viewport={viewport}
      data-flush={flush}
    >
      {hasHeader && (
        <header className={styles.header}>
          {title !== undefined && <h2 className={styles.title}>{title}</h2>}
          {count !== undefined && <span className={styles.count}>({count})</span>}
          <span className={styles.headSpacer} />
          {meta !== undefined && <div className={styles.meta}>{meta}</div>}
          {tools !== undefined && <div className={styles.tools}>{tools}</div>}
        </header>
      )}
      {loading && <div className={styles.loading} role="progressbar" aria-label="Loading" />}
      <div className={styles.body} data-pad={pad} data-scroll={scroll}>
        {children}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Section — a labelled group inside a rail or a panel body. The eyebrow always
   shares its line with something right-aligned (a count, a chevron, an age);
   a bare uppercase word floating above content is not a section header.
   --------------------------------------------------------------------------- */

export function Section({
  label,
  aside,
  children,
}: {
  label: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.eyebrow}>{label}</h3>
        <span className={styles.sectionSpacer} />
        {aside !== undefined && <div className={styles.sectionAside}>{aside}</div>}
      </div>
      {children}
    </section>
  );
}

/** The uppercase eyebrow on its own, for callers building their own header. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <span className={styles.eyebrow}>{children}</span>;
}

/**
 * Tiling helper. Panels are separated by a 3px seam — never 8, never 16.
 * `cols` accepts any grid-template-columns value.
 */
export function PanelGrid({
  cols,
  rows,
  className,
  children,
}: {
  cols?: string | undefined;
  rows?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div
      className={className === undefined ? styles.grid : `${styles.grid} ${className}`}
      style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}
    >
      {children}
    </div>
  );
}
